// Live "Claude is typing…" indicator. When the partial buffer contains
// text, we render it inline in the SAME style as `AssistantMessage` so
// the moment the persisted message lands the visual change is
// imperceptible. The signal that the answer is still streaming is the
// blinking caret at the end (and, before any text arrives, the three
// pulsing dots).
//
// Sprint messages-section-fixes (D-msg-9) — pre-fix the indicator
// shipped with `border-dashed`, `italic`, `bg-background/40` and a
// muted-foreground colour. When the stream finished, those styles
// vanished in a single frame, producing a visible jump that violated
// founder-first §5 ("calma, declarativa"). The indicator now matches
// `AssistantMessage` typography exactly; only the trailing caret
// communicates "still streaming".

import { t } from '../i18n';
import { Loader } from './ai-elements/loader';

export interface PartialIndicatorProps {
  text: string;
}

export function PartialIndicator({ text }: PartialIndicatorProps) {
  return (
    <div
      className="max-w-none text-chat text-foreground leading-relaxed"
      data-testid="partial-indicator"
    >
      {text.length === 0 ? (
        // Sprint ai-elements-rendering (Phase 2, T+X) — the pre-text state
        // swaps the three pulsing dots for the AI Elements spoke Loader
        // (ADR D-026). Same behaviour: a loading signal renders whenever
        // the buffer is empty, with the same aria-label.
        <span
          className="inline-flex items-center text-muted-foreground"
          role="status"
          aria-label={t('partialIndicator.ariaLabel')}
        >
          <Loader size={16} />
        </span>
      ) : (
        <span className="whitespace-pre-wrap break-words">
          {text}
          <BlinkingCaret />
        </span>
      )}
    </div>
  );
}

function BlinkingCaret() {
  return (
    <span
      className="inline-block w-px h-3 bg-muted-foreground align-text-bottom ml-0.5 animate-pulse"
      aria-hidden="true"
    />
  );
}
